import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'core/store';
import { Route, useLocation, useHistory, Redirect } from 'react-router';
import {
  logout,
  setAuthEmail,
  setAuthIdToken,
  setError,
  setLoginDest,
  setLoginMethod
} from 'features/shared/login/LoginSlice';
import { useDispatch } from 'react-redux';
import { resetAppToInitialState } from 'features/shared/login/LoginActions';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { getBaseApiUrl, getCurrentUser, getIdToken } from 'core/util';
import { getApp } from 'firebase/app';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { signInWithCustomToken } from 'firebase/auth';
import { getFirebaseAuth } from 'core/hooks/useFirebase';
import { AUTH_CUSTOM_TOKEN_URL } from 'core/constants';
import LoadingSpinner, { LoadingMode } from 'components/loading/LoadingSpinner';

// Firebase errors that mean "couldn't check right now", not "token revoked".
const TRANSIENT_TOKEN_REFRESH_ERRORS = [
  'auth/quota-exceeded',
  'auth/too-many-requests',
  'auth/network-request-failed',
  'auth/internal-error'
];

interface PrivateRouteProps {
  exact?: boolean;
  path: string;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, ...rest }) => {
  let location = useLocation();
  let history = useHistory();
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(
    (state: RootState) => state.login.loggedIn
  );

  const [isResolvingLogin, setIsResolvingLogin] = useState(!isAuthenticated);

  const redirectToLogin = () => {
    history.push('/login');
  };

  /**
   * Extracts the 'link_token' parameter from the current URL's query string.
   *
   * @returns {string | null} The value of the 'link_token' parameter if present, otherwise null.
   */

  const getLinkTokenFromUrl = (): string | null => {
    const params = new URLSearchParams(window.location.search);
    return params.get('link_token');
  };

  /**
   * Retrieves and dispatches an ID token for the given email.
   *
   * @param {string} email - The email address for which to obtain the ID token.
   */
  const getIdTokenForEmail = (email: string) => {
    dispatch(resetAppToInitialState());
    dispatch(setAuthEmail(email));
    getIdToken().then((token) => {
      dispatch(setAuthIdToken(token));
    });
  };

  /**
   * Fetches a custom Firebase token for the given link token.
   *
   * @param {string} linkToken - The link token obtained from the 'link_token' parameter in the current URL's query string.
   * @returns {Promise<string>} A promise which resolves with the custom Firebase token.
   * @throws {Error} If the request fails, an error is thrown with a message indicating the failure.
   */
  const fetchCustomToken = async (linkToken: string): Promise<string> => {
    const response = await fetch(getBaseApiUrl() + AUTH_CUSTOM_TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ link_token: linkToken })
    });
    if (!response.ok) {
      throw new Error('Failed to fetch custom token');
    }
    const { custom_token } = await response.json();
    return custom_token;
  };

  /**
   * Initiates the cross-domain login flow, given a custom Firebase token.
   *
   * @param {string} customToken - The custom Firebase token to use for the sign-in.
   *
   * The function attempts to sign-in with the token using the @capacitor-firebase/authentication
   * package. If the sign-in is successful, the user is redirected to the /checkpoint route.
   * Otherwise, the user is redirected to the login page.
   */
  const initiateCrossDomainLogin = async (customToken: string) => {
    try {
      await firebaseCustomTokenSignIn(customToken);
      history.push('/checkpoint');
    } catch (err) {
      console.error('Token sign-in failed:', err);
      redirectToLogin();
    }
  };

  /**
   * Initiates the Firebase custom token sign-in flow.
   *
   * @param {string} token - The custom Firebase token to use for the sign-in.
   */
  const firebaseCustomTokenSignIn = async (token: string) => {
    dispatch(setLoginMethod('email'));
    dispatch(setError(null));
    const result = await FirebaseAuthentication.signInWithCustomToken({
      token
    }).catch((error) => {
      dispatch(setError('Sign in failed. Please try again.'));
    });
    if (!!result) {
      const auth = getFirebaseAuth();
      getCurrentUser().then((result) => {
        if (!!result.user) {
          getIdTokenForEmail(result.user.email!);
        }
      });
      await signInWithCustomToken(auth, token);
    }
  };

  const checkTokenRevocation = async () => {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;

    if (!user) return;

    try {
      // Force refresh the ID token — this is what checks if it's still valid
      const token = await user.getIdToken(true);
    } catch (err: any) {
      // Transient failures say nothing about revocation. Logging out on them
      // loops: Login immediately restores the session, PrivateRoute remounts and
      // force-refreshes again, burning more quota each pass.
      if (TRANSIENT_TOKEN_REFRESH_ERRORS.includes(err?.code)) {
        console.warn('Token revocation check skipped:', err?.code);
        return;
      }
      console.error('Token refresh failed:', err);

      // Any other token refresh failure indicates the user should be logged out
      // This covers revoked tokens, expired tokens, disabled accounts, etc.
      logoutUser();
      throw new Error('Token validation failed'); // Throw to interrupt the authentication flow
    }
  };

  const logoutUser = () => {
    firebase.auth().signOut().then();
    dispatch(logout());
    dispatch(resetAppToInitialState());
    history.push(`/`);
  };

  useEffect(() => {
    // Set the login destination if the user is not authenticated and not on the login page
    if (!isAuthenticated && location.pathname !== '/login') {
      dispatch(setLoginDest(location.pathname));
    }

    /**
     * Handles the complete authentication flow including:
     * 1. Token revocation checking for existing users
     * 2. Cross-domain login flow for new sessions
     */
    const handleAuthentication = async () => {
      try {
        // First, always check if the current Firebase token is revoked from password resets
        await checkTokenRevocation();

        // If user is authenticated after token check, we're done
        if (isAuthenticated) {
          setIsResolvingLogin(false);
          return;
        }

        // If not authenticated, check for cross-domain login from AW
        const linkToken = getLinkTokenFromUrl();
        if (!linkToken) {
          redirectToLogin();
          return;
        }

        // Proceed with cross-domain login flow
        await performCrossDomainLogin(linkToken);
      } catch (err) {
        console.error('Authentication failed:', err);
        redirectToLogin();
      } finally {
        setIsResolvingLogin(false);
      }
    };

    handleAuthentication();
  }, []);

  /**
   * Handles the cross-domain login flow separately from token revocation
   */
  const performCrossDomainLogin = async (linkToken: string) => {
    try {
      const customToken = await fetchCustomToken(linkToken);
      await initiateCrossDomainLogin(customToken);
    } catch (err) {
      console.error('Cross-domain login failed:', err);
      throw err; // Re-throw to be handled by the main authentication flow
    }
  };

  if (isResolvingLogin) {
    return <LoadingSpinner mode={LoadingMode.Logo} />;
  }

  return (
    <Route
      {...rest}
      render={({ location }) =>
        isAuthenticated ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: location }
            }}
          />
        )
      }
    />
  );
};

export default PrivateRoute;
